import type {
  ClientMessage,
  ContactFormLabels,
  ServerMessage,
  HistoryMessage,
  MessageSource,
} from "../types/messages";

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000];
const MAX_RECONNECT_ATTEMPTS = 5;
const SESSION_STORAGE_KEY = "lizadocs_session_id";
// Small bound: only covers the connect()-to-open handshake window (~300ms),
// not an offline session's worth of typing.
const MAX_PENDING_OUTBOUND = 10;

export interface WebSocketCallbacks {
  onChunk: (content: string) => void;
  onDone: (messageId: string, sources?: MessageSource[]) => void;
  onError: (message: string) => void;
  onHistory: (messages: HistoryMessage[]) => void;
  onConnectionChange: (connected: boolean) => void;
  onRequestContactForm: (labels?: ContactFormLabels) => void;
  onContactFormReceived: () => void;
  onPreChatReceived: () => void;
}

/**
 * WebSocket client with automatic reconnection and session management.
 */
export class WidgetWebSocket {
  private socket: WebSocket | null = null;
  private widgetId: string;
  private apiBaseUrl: string;
  private callbacks: WebSocketCallbacks;
  private reconnectAttempt = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private sessionId: string;
  private isDestroyed = false;
  private isPreviewMode: boolean;
  private receivedErrorBeforeClose = false;
  private pendingOutbound: string[] = [];

  constructor(
    widgetId: string,
    callbacks: WebSocketCallbacks,
    apiBaseUrl: string = "wss://api.lizadocs.com/api",
    isPreviewMode: boolean = false,
  ) {
    this.widgetId = widgetId;
    this.apiBaseUrl = apiBaseUrl.replace(/^http/, "ws"); // Ensure ws/wss
    this.callbacks = callbacks;
    this.isPreviewMode = isPreviewMode;
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
   * Get existing session ID or create new one.
   * In preview mode, always create fresh session (not persisted).
   * In production, persisted in localStorage for conversation continuity.
   */
  private getOrCreateSessionId(): string {
    // Preview mode: fresh session each time, no persistence
    if (this.isPreviewMode) {
      return crypto.randomUUID();
    }

    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) return stored;

    const newId = crypto.randomUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, newId);
    return newId;
  }

  /**
   * Connect to the WebSocket server.
   */
  connect(): void {
    if (this.isDestroyed) return;
    // CONNECTING counts as done: calling disconnect() on an in-flight
    // handshake would abort it and double-connect (open() and a fast first
    // send both call this within the same handshake window).
    if (
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // Close existing socket if any
    this.disconnect();

    const url = `${this.apiBaseUrl}/ws/${this.widgetId}/chat`;

    try {
      this.socket = new WebSocket(url);
      this.setupEventHandlers();
    } catch (error) {
      console.error("[LizaDocs] WebSocket connection failed:", error);
      this.scheduleReconnect();
    }
  }

  /**
   * Set up WebSocket event handlers.
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log("[LizaDocs] WebSocket connected");
      this.reconnectAttempt = 0;
      this.receivedErrorBeforeClose = false;
      this.callbacks.onConnectionChange(true);
      this.flushPendingOutbound();
    };

    this.socket.onclose = (event) => {
      console.log("[LizaDocs] WebSocket closed:", event.code, event.reason);
      this.callbacks.onConnectionChange(false);

      const isFatalClose =
        event.code === 1000 ||
        event.code >= 4000 ||
        this.receivedErrorBeforeClose ||
        this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS;

      this.receivedErrorBeforeClose = false;

      if (!this.isDestroyed && !isFatalClose) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error("[LizaDocs] WebSocket error:", error);
    };

    this.socket.onmessage = (event) => {
      this.handleMessage(event.data);
    };
  }

  /**
   * Parse and handle incoming WebSocket messages.
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as ServerMessage;

      switch (message.type) {
        case "chunk":
          this.callbacks.onChunk(message.content);
          break;
        case "done":
          this.receivedErrorBeforeClose = false;
          this.callbacks.onDone(message.message_id, message.sources);
          break;
        case "error":
          this.receivedErrorBeforeClose = true;
          this.callbacks.onError(message.message);
          break;
        case "history":
          this.receivedErrorBeforeClose = false;
          this.callbacks.onHistory(message.messages);
          break;
        case "request_contact_form":
          this.callbacks.onRequestContactForm(message.labels);
          break;
        case "contact_form_received":
          this.callbacks.onContactFormReceived();
          break;
        case "pre_chat_received":
          this.callbacks.onPreChatReceived();
          break;
        default:
          console.warn("[LizaDocs] Unknown message type:", message);
      }
    } catch (error) {
      console.error("[LizaDocs] Failed to parse WebSocket message:", error);
    }
  }

  /**
   * Send a chat message.
   */
  send(content: string): void {
    this.dispatch({
      type: "message",
      session_id: this.sessionId,
      content,
    });
  }

  /**
   * Send contact info collected from the visitor.
   */
  sendContactInfo(name: string, email: string): void {
    this.dispatch({
      type: "contact_info",
      session_id: this.sessionId,
      name,
      email,
    });
  }

  /**
   * Send pre-chat contact info (collected upfront, name + email + optional phone).
   * Backend will create the conversation if it does not yet exist, so this can
   * be called before any chat message has been sent.
   */
  sendPreChatInfo(name: string, email: string, phone?: string): void {
    this.dispatch({
      type: "pre_chat_info",
      session_id: this.sessionId,
      name,
      email,
      ...(phone ? { phone } : {}),
    });
  }

  /**
   * Send now if connected; queue while a handshake or scheduled reconnect is
   * in flight (the widget connects lazily on open, so the visitor's first
   * message can race the connection). Anything else is a real failure.
   */
  private dispatch(message: ClientMessage): void {
    const payload = JSON.stringify(message);

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(payload);
      return;
    }

    const connectionPending =
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.reconnectTimeout !== null;

    if (!this.isDestroyed && connectionPending) {
      if (this.pendingOutbound.length >= MAX_PENDING_OUTBOUND) {
        this.callbacks.onError("Not connected. Please try again.");
        return;
      }
      this.pendingOutbound.push(payload);
      return;
    }

    console.warn("[LizaDocs] Cannot send: WebSocket not connected");
    this.callbacks.onError("Not connected. Please try again.");
  }

  /**
   * Flush messages queued while the handshake was in flight.
   */
  private flushPendingOutbound(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const queued = this.pendingOutbound;
    this.pendingOutbound = [];
    for (const payload of queued) {
      this.socket.send(payload);
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff.
   */
  private scheduleReconnect(): void {
    if (this.isDestroyed || this.reconnectTimeout) return;

    const delay =
      RECONNECT_DELAYS[
        Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)
      ];
    console.log(
      `[LizaDocs] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt + 1})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempt++;
      this.connect();
    }, delay);
  }

  /**
   * Disconnect from the WebSocket server.
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.onclose = null; // Prevent reconnect on manual close
      this.socket.close(1000, "Client closing");
      this.socket = null;
    }
  }

  /**
   * Destroy the WebSocket client permanently.
   */
  destroy(): void {
    this.isDestroyed = true;
    this.pendingOutbound = [];
    this.disconnect();
  }

  /**
   * Check if currently connected.
   */
  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}
